import { inject } from '@angular/core';
import type { ActivatedRouteSnapshot, CanActivateFn, Params, Routes } from '@angular/router';
import { RedirectCommand, Router } from '@angular/router';

import { IS_RPGC } from '@rpgc-ai/environments';

export const MY_PROFILE_DEFAULT_ROUTE = 'balance-history';

export const MY_PROFILE_CHILD_ROUTES: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: MY_PROFILE_DEFAULT_ROUTE,
  },
  {
    path: 'balance-history',
    loadComponent: () =>
      // eslint-disable-next-line @nx/enforce-module-boundaries
      import('@rpgc-ai/blocks/my-profile-tabs/components/balance-history/balance-history.component').then((m) => m.BalanceHistoryComponent),
  },
  {
    path: 'purchase-history',
    loadComponent: () =>
      // eslint-disable-next-line @nx/enforce-module-boundaries
      import('@rpgc-ai/blocks/my-profile-tabs/components/purchase-history/purchase-history.component').then(
        (m) => m.PurchaseHistoryComponent,
      ),
  },
  {
    path: 'referral',
    // eslint-disable-next-line @nx/enforce-module-boundaries
    loadComponent: () => import('@rpgc-ai/blocks/my-profile-tabs/components/referral/referral.component').then((m) => m.ReferralComponent),
  },
  {
    path: 'referral/:campaignId',
    // eslint-disable-next-line @nx/enforce-module-boundaries
    loadComponent: () => import('@rpgc-ai/blocks/my-profile-tabs/components/referral/referral.component').then((m) => m.ReferralComponent),
  },
  {
    path: 'monetization',
    pathMatch: 'full',
    redirectTo: 'monetization/chatbots',
  },
  {
    path: 'monetization/chatbots',
    loadComponent: () =>
      // eslint-disable-next-line @nx/enforce-module-boundaries
      import('@rpgc-ai/blocks/my-profile-tabs/components/monetization/monetization.component').then((m) => m.MonetizationComponent),
  },
  {
    path: 'monetization/billing',
    loadComponent: () =>
      // eslint-disable-next-line @nx/enforce-module-boundaries
      import('@rpgc-ai/blocks/my-profile-tabs/components/monetization/monetization.component').then((m) => m.MonetizationComponent),
  },
];

const PROFILE_ROUTE_IDS = new Set(['balance-history', 'purchase-history', 'referral', 'monetization']);
const MONETIZATION_SECTIONS = new Set(['chatbots', 'billing']);

function getLegacyProfileRoute(queryParams: Params, isRpgc: boolean): string | null {
  const preferredValue = isRpgc ? (queryParams['tab'] ?? queryParams['profile-tab']) : (queryParams['profile-tab'] ?? queryParams['tab']);

  return typeof preferredValue === 'string' && PROFILE_ROUTE_IDS.has(preferredValue) ? preferredValue : null;
}

function getLanguage(route: ActivatedRouteSnapshot): string | null {
  let current: ActivatedRouteSnapshot | null = route;

  while (current) {
    const language = current.paramMap.get('lang');
    if (language) {
      return language;
    }
    current = current.parent;
  }

  return null;
}

export const legacyProfileTabRedirectGuard: CanActivateFn = (route) => {
  const profileRoute = getLegacyProfileRoute(route.queryParams, inject(IS_RPGC, { optional: true }) ?? false);
  const language = getLanguage(route);

  if (!profileRoute || !language) {
    return true;
  }

  const router = inject(Router);
  const queryParams = { ...route.queryParams };
  delete queryParams['tab'];
  delete queryParams['profile-tab'];

  const profilePath = [profileRoute];

  if (profileRoute === 'referral') {
    delete queryParams['table'];

    if (typeof queryParams['id'] === 'string' && queryParams['id']) {
      profilePath.push(queryParams['id']);
      delete queryParams['id'];
    }
  }

  if (profileRoute === 'monetization') {
    const legacySection = queryParams['monetization-section'] ?? queryParams['section'];
    profilePath.push(typeof legacySection === 'string' && MONETIZATION_SECTIONS.has(legacySection) ? legacySection : 'chatbots');
    delete queryParams['monetization-section'];
    delete queryParams['section'];
  }

  const redirectTo = router.createUrlTree(['/', language, 'my-profile', ...profilePath], {
    queryParams,
    fragment: route.fragment ?? undefined,
  });

  return new RedirectCommand(redirectTo, { replaceUrl: true });
};
